// Move sounds and spoken move announcements.
// Sound effects are synthesized with the Web Audio API (no asset files); spoken
// moves use the browser's SpeechSynthesis. Both are opt-in/out via the toggles in
// SoundControls and persisted in localStorage.

let audioCtx = null;

function getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

// --- preferences (localStorage) ---
const SOUND_KEY = 'cb_sound'; // default ON
const VOICE_KEY = 'cb_voice'; // default OFF

export const isSoundOn = () => localStorage.getItem(SOUND_KEY) !== 'off';
export const isVoiceOn = () => localStorage.getItem(VOICE_KEY) === 'on';
export const setSoundOn = (on) => localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
export const setVoiceOn = (on) => localStorage.setItem(VOICE_KEY, on ? 'on' : 'off');

// Resume the audio context from a user gesture (browsers start it suspended).
export const unlockAudio = () => { getCtx(); };

// --- synthesis primitives ---
function noise({ freq = 800, q = 1, dur = 0.06, gain = 0.25 }) {
  const ctx = getCtx();
  if (!ctx) return;
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = freq;
  bp.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
  src.connect(bp).connect(g).connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + dur);
}

function tone({ freq = 660, dur = 0.12, type = 'sine', gain = 0.18, when = 0 }) {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + dur);
}

const sfx = {
  move: () => noise({ freq: 850, dur: 0.05, gain: 0.22 }),
  capture: () => noise({ freq: 380, q: 0.7, dur: 0.1, gain: 0.32 }),
  check: () => { tone({ freq: 720, dur: 0.09 }); tone({ freq: 720, dur: 0.09, when: 0.11 }); },
  end: () => { tone({ freq: 520, dur: 0.16 }); tone({ freq: 350, dur: 0.22, when: 0.16 }); },
};

// Play a confirmation click (used when enabling the toggle).
export const previewSound = () => { if (isSoundOn()) sfx.move(); };

// --- SAN -> spoken English ---
const PIECE = { K: 'king', Q: 'queen', R: 'rook', B: 'bishop', N: 'knight' };
const NUM = { 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight' };
const spellSquare = (sq) => `${sq[0]} ${NUM[sq[1]] || sq[1]}`;

export function sanToSpeech(san) {
  if (!san) return '';
  if (/^(O-O-O|0-0-0)/.test(san)) return finishCheck('castles queenside', san);
  if (/^(O-O|0-0)/.test(san)) return finishCheck('castles kingside', san);

  let core = san.replace(/[+#]/g, '');
  let promo = '';
  const eq = core.indexOf('=');
  if (eq !== -1) { promo = ` promotes to ${PIECE[core[eq + 1]] || ''}`; core = core.slice(0, eq); }

  let pieceWord = '';
  if (/^[KQRBN]/.test(core)) { pieceWord = `${PIECE[core[0]]} `; core = core.slice(1); }
  const takes = core.includes('x');
  core = core.replace('x', '');
  const dest = core.slice(-2);
  const disambig = core.slice(0, -2);

  const text = `${pieceWord}${disambig ? disambig + ' ' : ''}${takes ? 'takes ' : ''}${spellSquare(dest)}${promo}`;
  return finishCheck(text, san);
}

function finishCheck(text, san) {
  if (san.includes('#')) return `${text}, checkmate`;
  if (san.includes('+')) return `${text}, check`;
  return text;
}

export function speak(text) {
  if (!('speechSynthesis' in window) || !text) return;
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.95;
  window.speechSynthesis.cancel(); // don't let announcements pile up
  window.speechSynthesis.speak(u);
}

export const previewVoice = () => { if (isVoiceOn()) speak('Move announcements on'); };

// --- the one call sites use: react to a move that was just applied ---
// move: chess.js move result (has .san, .captured). game: the chess.js instance after the move.
export function onMove(move, game) {
  if (!move) return;
  if (isSoundOn()) {
    if (game.isGameOver()) sfx.end();
    else if (game.isCheck()) sfx.check();
    else if (move.captured) sfx.capture();
    else sfx.move();
  }
  if (isVoiceOn()) speak(sanToSpeech(move.san));
}
